/**
 * Framework-free emoji-picker data shaping, shared by the client DM picker and
 * the town-square community picker (both render their own React grid but agree
 * on which emojis to show and in what order).
 *
 * Two problems this fixes in the raw `@tiptap/extension-emoji` dataset:
 *  - it begins with 26 `regional_indicator_*` entries that render as boxed A–Z
 *    "letters" (a lone regional indicator has no flag pair) — we drop them;
 *  - it has no notion of favorites/most-used — we pin a fixed favorites row on
 *    top so the emojis people actually reach for (👍 ❤️ 😂 🔥 …) are first.
 */

import { normalizeReactionEmoji } from './emoji-validation';

/** Minimal shape of a raw `@tiptap/extension-emoji` entry. */
export interface RawEmoji {
  name: string;
  emoji?: string;
  group?: string;
  shortcodes?: string[];
  tags?: string[];
}

export interface EmojiEntry {
  name: string;
  emoji: string;
  group: string;
  shortcode: string;
  tags: string[];
}

export interface EmojiGroup {
  /** Stable React key. */
  key: string;
  /** Heading shown above the group (empty string ⇒ render no heading). */
  label: string;
  items: EmojiEntry[];
}

// The top row, by tiptap emoji `name` (with common shortcode aliases as a
// fallback match). Order is the display order. Any key that doesn't resolve to
// a real emoji in the dataset is simply skipped — never rendered blank.
const FAVORITE_KEYS = [
  '+1', // 👍
  'heart', // ❤️
  'joy', // 😂
  'fire', // 🔥
  'tada', // 🎉
  'pray', // 🙏
  'heart_eyes', // 😍
  'smile', // 😄
  'open_mouth', // 😮
  'cry', // 😢
  'clap', // 👏
  'white_check_mark', // ✅
];

// Group display order (dataset uses lowercase group names; the empty group that
// holds the smiley faces is relabeled to a real heading).
const SMILEYS_LABEL = 'Smileys & Emotion';
const GROUP_ORDER = [
  SMILEYS_LABEL,
  'people & body',
  'animals & nature',
  'food & drink',
  'activities',
  'travel & places',
  'objects',
  'symbols',
  'flags',
];

/**
 * Normalize + prune the raw dataset: drop the regional-indicator "letters" and
 * skin-tone `components`, drop entries with no printable glyph (e.g. GitHub
 * image shortcodes), and relabel the empty face group.
 */
export function buildEmojiEntries(raw: RawEmoji[]): EmojiEntry[] {
  return raw
    .filter((e) => !e.name.startsWith('regional_indicator_'))
    .filter((e) => (e.group ?? '') !== 'components')
    .map((e) => ({
      name: e.name,
      // The dataset omits the variation selector on several hundred entries, so its
      // "heart" is a bare U+2764 — which renders as a TEXT glyph (a black heart outline)
      // rather than the red emoji, and is a different string from the one the reaction
      // palette uses. Qualifying here fixes what the grid draws and what it hands back.
      emoji: normalizeReactionEmoji(e.emoji ?? ''),
      group: e.group && e.group.trim() ? e.group : SMILEYS_LABEL,
      shortcode: e.shortcodes?.[0] ?? e.name,
      tags: e.tags ?? [],
    }))
    .filter((e) => e.emoji);
}

/**
 * Resolve one favorite key to an entry, STRONGEST MATCH FIRST.
 *
 * A single first-wins pass over name/shortcode/tags picks whichever entry happens to sit
 * earliest in the dataset, and a tag is the weakest of the three signals — so 'heart'
 * resolved to 😻 (tagged "heart") rather than ❤️, and 'fire' to 🧑‍🚒 (tagged "fire")
 * rather than 🔥. The favorites row is the fast path for exactly the emoji people reach
 * for most, so handing them a cat instead is the one thing it must not do.
 */
function findFavorite(entries: EmojiEntry[], key: string): EmojiEntry | undefined {
  return (
    entries.find((entry) => entry.name === key) ??
    entries.find((entry) => entry.shortcode === key) ??
    entries.find((entry) => entry.tags.includes(key))
  );
}

/**
 * Build the grouped list the picker renders. With a search term it returns a
 * single flat "results" group; otherwise it returns a pinned favorites row
 * followed by the ordered category groups.
 */
export function buildEmojiGroups(raw: RawEmoji[], search: string): EmojiGroup[] {
  const entries = buildEmojiEntries(raw);
  const q = search.trim().toLowerCase();

  if (q) {
    const matches = entries.filter(
      (e) => e.name.includes(q) || e.shortcode.includes(q) || e.tags.some((tag) => tag.includes(q))
    );
    return matches.length ? [{ key: 'search', label: '', items: matches }] : [];
  }

  const groups: EmojiGroup[] = [];

  const favorites = FAVORITE_KEYS.map((key) => findFavorite(entries, key)).filter((e): e is EmojiEntry => Boolean(e));
  if (favorites.length) {
    groups.push({ key: 'favorites', label: 'Frequently used', items: favorites });
  }

  const byGroup = new Map<string, EmojiEntry[]>();
  for (const entry of entries) {
    if (!byGroup.has(entry.group)) byGroup.set(entry.group, []);
    byGroup.get(entry.group)!.push(entry);
  }

  const rank = (group: string): number => {
    const i = GROUP_ORDER.indexOf(group);
    return i === -1 ? GROUP_ORDER.length : i;
  };
  const orderedGroups = [...byGroup.keys()].sort((a, b) => rank(a) - rank(b));
  for (const group of orderedGroups) {
    groups.push({ key: group, label: group, items: byGroup.get(group)! });
  }

  return groups;
}
